import { Injectable, computed, effect, inject, signal } from '@angular/core';
import type { WritableSignal } from '@angular/core';
import {
  MILESTONES,
  SETUP_HALLMARK_ID,
  groupContaining,
  hasFullyDrainedObservable,
  isGroupHallmark,
  isMilestoneComplete,
} from '@app/content/milestones';
import type {
  MilestoneContext,
  MilestoneDef,
  SingleMilestoneAnchor,
} from '@app/content/milestones';
import { isInsideBase } from '@sim/core/base';
import { isObservableConnectedToBase } from '@sim/core/routing';
import { samePos } from '@sim/core/types';
import type { GridPos } from '@sim/core/types';
import { DebugModeService } from './debug-mode.service';
import { clearSeenIds, loadSeenIds, saveSeenIds } from './onboarding-save';
import { SimEngineService } from './sim-engine.service';

export interface LoggedMilestone extends MilestoneDef {
  seen: boolean;
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly engine = inject(SimEngineService);
  private readonly debug = inject(DebugModeService);
  private readonly seenSignal = signal<Set<string>>(loadSeenIds());
  private readonly hasLeaked = signal(false);
  private readonly hasUnsubscribedOnce = signal(false);
  private readonly hasSlagReachedSubscriber = signal(false);
  private readonly hasSubscriptionErrored = signal(false);

  readonly context = computed<MilestoneContext>(() => ({
    state: this.engine.state(),
    hasLeakedBefore: this.hasLeaked(),
    hasUnsubscribedOnce: this.hasUnsubscribedOnce(),
    hasSlagReachedSubscriber: this.hasSlagReachedSubscriber(),
    hasSubscriptionErrored: this.hasSubscriptionErrored(),
  }));

  private readonly pending = computed<MilestoneDef[]>(() => {
    const ctx = this.context();
    const seen = this.seenSignal();
    return MILESTONES.filter((m) => !seen.has(m.id) && m.isTriggered(ctx));
  });

  readonly active = computed<MilestoneDef | null>(() =>
    this.debug.enabled() ? null : (this.pending()[0] ?? null),
  );

  /** True once the scripted setup arc (pick tool -> place -> pipe -> connect -> subscribe) is done. */
  readonly isSetupComplete = computed(
    () => this.debug.enabled() || this.seenSignal().has(SETUP_HALLMARK_ID),
  );

  /**
   * Only a `walkthrough`-intensity milestone pauses the sim — it owns the map/spotlight lockout,
   * so nothing should tick underneath it while the player reads, acts, or has just dismissed it.
   * `notice`/`achievement` milestones are lightweight and non-blocking by design (see BACKLOG.md's
   * v1 design guidelines): everything, including the sim clock, stays live under them.
   */
  readonly shouldPauseSim = computed(() => this.active()?.intensity === 'walkthrough');

  /**
   * True while a full-screen, unanchored walkthrough splash (`welcome`, `operator-hallmark`, …)
   * is up — the one variant of coach-mark that owns the whole viewport. Anchored walkthrough
   * steps (e.g. `configure-observable`) are meant to coexist with whatever they're pointing at, so
   * only the unanchored kind should ever pre-empt another overlay like an arg picker.
   */
  readonly isBlockingSplashActive = computed(() => {
    const milestone = this.active();
    return (
      milestone !== null &&
      milestone.intensity === 'walkthrough' &&
      milestone.anchor.kind === 'none'
    );
  });

  readonly log = computed<LoggedMilestone[]>(() => {
    const ctx = this.context();
    const seen = this.seenSignal();
    return MILESTONES.filter((m) => m.isTriggered(ctx) || seen.has(m.id)).map((m) => ({
      ...m,
      seen: seen.has(m.id),
    }));
  });

  constructor() {
    effect(() => {
      if (hasFullyDrainedObservable(this.engine.state())) this.record(this.hasLeaked);
    });
    this.engine.subscriptionChanged.subscribe(({ subscribed }) => {
      if (!subscribed) this.record(this.hasUnsubscribedOnce);
    });
    this.engine.events.subscribe((events) => {
      if (events.some((e) => e.type === 'packetSold' && e.material === 'slag')) {
        this.record(this.hasSlagReachedSubscriber);
      }
      if (events.some((e) => e.type === 'subscriptionErrored')) {
        this.record(this.hasSubscriptionErrored);
      }
    });
    effect(() => {
      const ctx = this.context();
      const seen = this.seenSignal();
      if (this.debug.enabled()) return;
      for (const milestone of MILESTONES) {
        if (!seen.has(milestone.id) && isMilestoneComplete(milestone, ctx)) {
          this.dismiss(milestone.id);
        }
      }
    });
  }

  /** Debug mode is a scratch space on a fully-unlocked map: nothing played out in it may count as
   * onboarding the player has actually lived through. */
  private record(happened: WritableSignal<boolean>): void {
    if (!this.debug.enabled()) happened.set(true);
  }

  /** The observable is fixed in place while the scripted setup arc is still in progress. */
  canEraseObservable(): boolean {
    return this.isSetupComplete();
  }

  /** Subscribing is the intended *next* action once wired — not before, even if the engine allows it. */
  canSubscribe(): boolean {
    if (this.isSetupComplete()) return true;
    const state = this.engine.state();
    return Object.values(state.observables).some((observable) =>
      isObservableConnectedToBase(state, observable),
    );
  }

  /**
   * A spotlighted `walkthrough` milestone darkens everything outside its anchor(s), so the map
   * must reject hover/click there too — otherwise the game's own cursor highlight visibly reacts
   * to a cell the player can't actually act on. `{ spotlight: false }` milestones (e.g. wiring
   * pipes across the whole grid) opt out entirely, as does any `notice`/`achievement` milestone —
   * those are lightweight and non-blocking by design, so the map is always fully interactive
   * under them.
   */
  isMapInteractionAllowed(pos: GridPos): boolean {
    const milestone = this.active();
    if (
      !milestone ||
      milestone.intensity !== 'walkthrough' ||
      milestone.spotlight === false ||
      milestone.anchor.kind === 'none'
    ) {
      return true;
    }
    const anchors =
      milestone.anchor.kind === 'multi' ? milestone.anchor.anchors : [milestone.anchor];
    return anchors.some((anchor) => this.matchesAnchor(anchor, pos));
  }

  private matchesAnchor(anchor: SingleMilestoneAnchor, pos: GridPos): boolean {
    if (anchor.kind === 'dom') return false;
    if (anchor.kind === 'gridRect') {
      const rect = anchor.rect(this.context());
      return rect !== null && isInsideBase(rect, pos);
    }
    const target = anchor.pos(this.context());
    return target !== null && samePos(target, pos);
  }

  dismiss(id: string): void {
    this.seenSignal.update((prev) => new Set(prev).add(id));
    if (!groupContaining(id) || isGroupHallmark(id)) {
      saveSeenIds(this.seenSignal());
    }
  }

  reset(): void {
    this.seenSignal.set(new Set());
    this.hasLeaked.set(false);
    this.hasUnsubscribedOnce.set(false);
    this.hasSlagReachedSubscriber.set(false);
    this.hasSubscriptionErrored.set(false);
    clearSeenIds();
  }
}
